import { intl } from '@fullstory/intl';
import {
  Display,
  DisplayCurrencyType,
  DisplayDuration,
  DisplayDurationUnit,
  DisplayPrecision,
} from '@fullstory/proto/fs/services/webber/frontend/display';
import { assertExhaustive } from '@fullstory/typeutils';
import { shortNumber } from '@fullstory/util/fmt';
import { defineMessage, FormatNumberOptions } from 'react-intl';
import {
  currencyFromCurrencyType,
  formatCurrencyWithLocale,
  getCurrencyPlaceholder,
} from './currency';

export enum TimeDurations {
  Unknown = '',
  Nanoseconds = 'nanoseconds',
  Microseconds = 'microseconds',
  Milliseconds = 'milliseconds',
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
}

const durationFormatOptions: { [key in TimeDurations]: FormatNumberOptions } = {
  [TimeDurations.Unknown]: {},
  [TimeDurations.Nanoseconds]: {},
  [TimeDurations.Microseconds]: {},
  [TimeDurations.Milliseconds]: {
    style: 'unit',
    unit: 'millisecond',
    unitDisplay: 'narrow',
  },
  [TimeDurations.Seconds]: {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'narrow',
  },
  [TimeDurations.Minutes]: {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'narrow',
  },
  [TimeDurations.Hours]: {
    style: 'unit',
    unit: 'hour',
    unitDisplay: 'narrow',
  },
  [TimeDurations.Days]: {
    style: 'unit',
    unit: 'day',
    unitDisplay: 'narrow',
  },
};

const durationFormatNanoseconds = defineMessage({
  defaultMessage: '{value}ns',
  id: 'ewNtYs',
  description: 'nanoseconds. Value is already formatted for the current locale',
});
const durationFormatMicroseconds = defineMessage({
  defaultMessage: '{value}mu',
  id: '9pCSpl',
  description: 'microseconds. Value is already formatted for the current locale',
});

const durationFromDurationUnit = {
  [DisplayDurationUnit.DURATION_SEC]: TimeDurations.Seconds,
  [DisplayDurationUnit.DURATION_MS]: TimeDurations.Milliseconds,
  [DisplayDurationUnit.DURATION_MU]: TimeDurations.Microseconds,
  [DisplayDurationUnit.DURATION_NS]: TimeDurations.Nanoseconds,
  [DisplayDurationUnit.DURATION_INVALID]: TimeDurations.Milliseconds,
};

type durationMapType = {
  [key in TimeDurations]: {
    up?: TimeDurations;
    down?: TimeDurations;
    upFactor?: number;
    downFactor?: number;
    index: number;
    // numSubFormats is a number representing to what degree we can downscale the time presentation to, excluding
    // the current format. A few exapmles inline!
    numSubFormats: number;
  };
};

const durationMap: durationMapType = {
  [TimeDurations.Unknown]: {
    index: -1,
    numSubFormats: 0,
  },
  [TimeDurations.Days]: {
    down: TimeDurations.Hours,
    downFactor: 24,
    index: 6,
    // 1 subformat = days, hours
    numSubFormats: 1,
  },
  [TimeDurations.Hours]: {
    up: TimeDurations.Days,
    down: TimeDurations.Minutes,
    upFactor: 24,
    downFactor: 60,
    index: 5,
    // hours, minutes
    numSubFormats: 1,
  },
  [TimeDurations.Minutes]: {
    up: TimeDurations.Hours,
    down: TimeDurations.Seconds,
    upFactor: 60,
    downFactor: 60,
    index: 4,
    // minutes, seconds
    numSubFormats: 1,
  },
  [TimeDurations.Seconds]: {
    up: TimeDurations.Minutes,
    down: TimeDurations.Milliseconds,
    upFactor: 60,
    downFactor: 1000,
    index: 3,
    // seconds
    numSubFormats: 0,
  },
  [TimeDurations.Milliseconds]: {
    up: TimeDurations.Seconds,
    down: TimeDurations.Microseconds,
    upFactor: 1000,
    downFactor: 1000,
    index: 2,
    numSubFormats: 0,
  },
  [TimeDurations.Microseconds]: {
    up: TimeDurations.Milliseconds,
    down: TimeDurations.Nanoseconds,
    upFactor: 1000,
    downFactor: 1000,
    index: 1,
    numSubFormats: 0,
  },
  [TimeDurations.Nanoseconds]: {
    up: TimeDurations.Microseconds,
    upFactor: 1000,
    index: 0,
    numSubFormats: 0,
  },
};

// Given a list of values and a displayForamt, returns the best unit to convert the values to
export const determineDurationUnit = (values: number[], displayFormat: Display): TimeDurations => {
  if (!values || !values.length) return TimeDurations.Milliseconds;
  const nonZeroValues = values.filter(value => value !== 0);
  const smallestNonZero = nonZeroValues.length
    ? nonZeroValues.reduce((acc, val) => Math.min(acc, val))
    : 0;
  const desiredUnit = scaleDuration(smallestNonZero, displayFormat)[1];
  return desiredUnit;
};

export const scaleDuration = (
  value: number,
  displayFormat: Display | undefined,
): [number, TimeDurations] => {
  if (!displayFormat || !displayFormat.displayDuration) return [value, TimeDurations.Unknown];

  let calculatedVal = value;
  let unit = durationFromDurationUnit[displayFormat.displayDuration.unit];
  if (!displayFormat.displayDuration.autoscale) {
    return [value, unit];
  }

  // if value is large, scale up the units (ex: ms --> s)
  while (
    durationMap[unit].upFactor &&
    (calculatedVal >= durationMap[unit].upFactor! || calculatedVal <= -durationMap[unit].upFactor!)
  ) {
    const factor = durationMap[unit].upFactor;
    if (!factor) break;

    calculatedVal = calculatedVal / factor;

    if (!durationMap[unit].up) break;
    unit = durationMap[unit].up ?? unit;
  }

  // if value is exactly 0, return the given unit without scaling down
  // otherwise we get weird looking values like '0 ns' when the selected unit is s or ms
  if (calculatedVal === 0) {
    return [calculatedVal, unit];
  }

  // if value is small, scale down the units (ex: s --> ms)
  while (calculatedVal < 0.01 && calculatedVal > -0.01) {
    const factor = durationMap[unit].downFactor;
    if (!factor) break;

    calculatedVal = calculatedVal * factor;

    if (!durationMap[unit].down) break;
    unit = durationMap[unit].down ?? unit;
  }

  return [calculatedVal, unit];
};

// Takes in a single value, its current displayFormat and the desired duration unit and converts the
// value to the desired duration unit
export const scaleValueToDurationUnit = (
  value: number,
  displayFormat: Display | undefined,
  durationUnit: TimeDurations,
): number => {
  if (!displayFormat || !displayFormat.displayDuration || !displayFormat.displayDuration.autoscale)
    return value;

  let unit = durationFromDurationUnit[displayFormat.displayDuration.unit];

  const steps = durationMap[durationUnit].index - durationMap[unit].index;
  const useUpFactor = steps > 0;

  let calculatedVal = value;
  while (unit !== durationUnit) {
    const factor = useUpFactor ? durationMap[unit].upFactor : durationMap[unit].downFactor;
    if (!factor) break;

    calculatedVal = useUpFactor ? calculatedVal / factor : calculatedVal * factor;

    if (useUpFactor && !durationMap[unit].up) break;
    if (!useUpFactor && !durationMap[unit].down) break;
    unit = (useUpFactor ? durationMap[unit].up : durationMap[unit].down) || unit;
  }

  return calculatedVal;
};

export function formatCurrency(
  currency: DisplayCurrencyType,
  value: number | null,
  keepPrecision: boolean = false,
): string {
  const currencyCode = currencyFromCurrencyType[currency];
  if (!value) {
    return getCurrencyPlaceholder(currencyCode);
  }
  const roundedValue = +value.toFixed(2);

  if (keepPrecision) {
    return formatCurrencyWithLocale(roundedValue, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }

  if (value > 1000) {
    return formatCurrencyWithLocale(roundedValue, {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
      style: 'currency',
      currency: currencyCode,
    });
  }

  if (Number.isInteger(value)) {
    return formatCurrencyWithLocale(roundedValue, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  return formatCurrencyWithLocale(roundedValue, {
    style: 'currency',
    currency: currencyCode,
  });
}

export const displayToFormatNumberOptions = (
  display: Display | null | undefined,
): FormatNumberOptions => {
  // don't handle duration units
  if (!display || display.duration || display.displayDuration) return {};

  switch (display.displayPrecision) {
    case DisplayPrecision.PRECISION_INVALID:
    case DisplayPrecision.PRECISION_0:
      return {
        maximumFractionDigits: 0,
      };
    case DisplayPrecision.PRECISION_1: {
      const isCurrency = !!display.currency;
      if (isCurrency) {
        console.warn('Invalid precision for currency, defaulting to two decimal');
      }
      return {
        minimumFractionDigits: isCurrency ? 2 : 1,
        maximumFractionDigits: isCurrency ? 2 : 1,
      };
    }
    case DisplayPrecision.PRECISION_2:
      return {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
    default:
      assertExhaustive(display.displayPrecision);
  }
};

/**
 * Special cases numbers and revenue with a minimum precision of 1.
 * Otherwise pass through to displayToFormatNumberOptions.
 */
export const changePercentageDisplayToFormatNumberOptions = (
  display: Display | null | undefined,
): FormatNumberOptions => {
  // no display = default to 1
  // numbers and revenue use a minimum of 1.
  if (
    !display ||
    ((display?.number || display?.currency) &&
      [DisplayPrecision.PRECISION_INVALID, DisplayPrecision.PRECISION_0].includes(
        display.displayPrecision,
      ))
  ) {
    return {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    };
  }

  return displayToFormatNumberOptions(display);
};

export const formatNumber = (
  value: number | null,
  displayFormat: Display | undefined,
  durationUnit?: TimeDurations,
  forCSV: boolean = false,
): string => {
  if (typeof value !== 'number') return '';

  if (displayFormat?.number) {
    return intl.formatNumber(value, displayToFormatNumberOptions(displayFormat));
  }

  if (displayFormat?.percent || displayFormat?.percentUnmodified) {
    const conversionFactor = displayFormat?.percent ? 1 : 100;
    return intl.formatNumber(value / conversionFactor, {
      ...displayToFormatNumberOptions(displayFormat),
      style: 'percent',
    });
  }

  if (displayFormat?.currency) {
    const currency = currencyFromCurrencyType[displayFormat.currency];
    return formatCurrencyWithLocale(value, {
      ...displayToFormatNumberOptions(displayFormat),
      style: 'currency',
      currency,
    });
  }

  if (displayFormat?.displayDuration) {
    if (forCSV) {
      return formatDurationForCSV(value, displayFormat.displayDuration);
    } else {
      const { convertedValue, scaledUnit } = displayDurationUnits(
        value,
        displayFormat,
        durationUnit,
      );
      return formatDurationToPartials(convertedValue, scaledUnit);
    }
  }

  return intl.formatNumber(value);
};

export const formatShortNumber = (
  value: number | null,
  displayFormat: Display | undefined,
  durationUnit?: TimeDurations,
  forcePrecision: boolean = false,
): string => {
  if (typeof value !== 'number') return '';
  if (displayFormat?.number) {
    if (value < 1 && value > -1) {
      switch (displayFormat.displayPrecision) {
        case DisplayPrecision.PRECISION_INVALID:
        case DisplayPrecision.PRECISION_0:
          return '0';
        case DisplayPrecision.PRECISION_1:
          return shortNumber(value, {
            minimumFractionDigits: forcePrecision ? 1 : undefined,
            maximumFractionDigits: 1,
          });
        case DisplayPrecision.PRECISION_2:
          return shortNumber(value, {
            minimumFractionDigits: forcePrecision ? 2 : undefined,
            maximumFractionDigits: 2,
          });
        default:
          assertExhaustive(displayFormat.displayPrecision);
      }
    }
    return shortNumber(value);
  }

  // percentages and revenue both use the same fraction digits logic
  const opts = displayToFormatNumberOptions(displayFormat);
  opts.minimumFractionDigits = forcePrecision ? opts.maximumFractionDigits : undefined;

  if (displayFormat?.percent || displayFormat?.percentUnmodified) {
    const conversionFactor = displayFormat?.percent ? 1 : 100;
    return intl.formatNumber(value / conversionFactor, {
      ...opts,
      style: 'percent',
      notation: 'compact',
      compactDisplay: 'short',
    });
  }

  if (displayFormat?.displayDuration) {
    const { convertedValue, scaledUnit } = displayDurationUnits(value, displayFormat, durationUnit);
    return formatDurationToPartials(convertedValue, scaledUnit);
  }

  if (displayFormat?.currency) {
    const currency = currencyFromCurrencyType[displayFormat.currency];
    return formatCurrencyWithLocale(value, {
      ...opts,
      style: 'currency',
      currency,
      notation: 'compact',
      compactDisplay: 'short',
    });
  }

  return shortNumber(value);
};

/**
 * Formats AOV (average order value). Rules for this are:
 *  1. If over $1,000, then round (e.g. $12,234.23 -> $12,234)
 *  2. If under $1,000 then don't round (e.g. $23.45 -> $23.45)
 *
 * The full dollar amount is always shown (e.g., $12,340 isn't shorted to $12K or anything similar).
 */
export const formatAov = (currency: DisplayCurrencyType, value: number | null): string => {
  const currencyCode = currencyFromCurrencyType[currency];
  if (!value) {
    return getCurrencyPlaceholder(currencyCode);
  }
  const roundedValue = +value.toFixed(2);
  if (value > 1000) {
    return formatCurrencyWithLocale(roundedValue, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
      currency: currencyCode,
    });
  }

  return formatCurrencyWithLocale(roundedValue, {
    style: 'currency',
    currency: currencyCode,
  });
};

/**
 * Formats projected revenue. This is used to display values related to the revenue event. The rules are:
 * 1. If >= $1,000 then short format is used (e.g., $1,234 -> $1.2K)
 * 2. If &lt; $1,000 then full amount is shown without cents (e.g., $355.34 -> $355)
 *
 * @see {@link formatCurrency} to format currency not related to revenue event
 */
export const formatRevenue = (currency: DisplayCurrencyType, revenue: number | null): string => {
  const currencyCode = currencyFromCurrencyType[currency];
  if (!revenue) {
    return getCurrencyPlaceholder(currencyCode);
  }
  const roundedRevenue = +revenue.toFixed(2);
  if (revenue > 1000) {
    return formatCurrencyWithLocale(roundedRevenue, {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
      style: 'currency',
      currency: currencyCode,
    });
  }

  if (Number.isInteger(revenue)) {
    return formatCurrencyWithLocale(roundedRevenue, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }
  return formatCurrencyWithLocale(roundedRevenue, {
    style: 'currency',
    currency: currencyCode,
  });
};

export {
  getKnownClsUnit,
  getKnownDurationUnit,
  getKnownPercentUnmodifiedUnit,
  knownUnit,
} from '@fullstory/metrics-model';

const displayDurationUnits = (
  value: number,
  displayFormat: Display,
  durationUnit?: TimeDurations,
) => {
  let convertedValue: number;
  let scaledUnit: TimeDurations;
  // convert to a specific duration unit
  if (durationUnit) {
    scaledUnit = durationUnit;
    convertedValue = scaleValueToDurationUnit(value, displayFormat, durationUnit);
  } else {
    [convertedValue, scaledUnit] = scaleDuration(value, displayFormat);
  }

  return {
    convertedValue,
    scaledUnit,
  };
};

const formatDuration = (value: number, unit: TimeDurations, fractionDigits: number) => {
  const options: FormatNumberOptions = {
    ...durationFormatOptions[unit],
    maximumFractionDigits: fractionDigits,
  };

  let result: string = intl.formatNumber(value, options);

  // formatNumber does not support nanoseconds or microseconds, so append the unit to those manually.
  if (unit == TimeDurations.Nanoseconds) {
    result = intl.formatMessage(durationFormatNanoseconds, { value: result });
  }
  if (unit == TimeDurations.Microseconds) {
    result = intl.formatMessage(durationFormatMicroseconds, { value: result });
  }
  return result;
};
/*
 * This function takes in a float value (eg. 1.123) and a scaled unit (eg. TimeDurations.Hours),
 * and then translates it into a series of smaller representations of that time (hours, minutes, seconds, etc.)
 */
export const formatDurationToPartials = (
  value: number,
  scaledUnit: TimeDurations,
  shortNumberDisplay?: boolean,
): string => {
  const { numSubFormats } = durationMap[scaledUnit];
  const subFormats = getSubformats(scaledUnit, numSubFormats);
  let string = '';

  let valueParsing = value;
  for (let i = 0; i < subFormats.length; i++) {
    const subFormat = subFormats[i]!;

    // The integer will be used to calculate the dispaly for the current unit, while the remainder
    // is used for the next unit in scale.
    const [integer, remainder] = getIntegerAndRemainder(valueParsing);
    if (integer < 1) {
      // if the initial unit given is below 1, just render that and exit
      if (i === 0) {
        return formatDuration(valueParsing, subFormat, 2);
      }
      // otherwise, we have had units before this, so we can skip the smaller units
      break;
    }

    let fractionDigits = 0;
    let valueToFormat = integer;
    // if we're on the last format that we know about, print the integer+remainder
    // as a formatted decimal
    if (i === subFormats.length - 1) {
      // Round the last unit if there are multiple formatters (e.g. never show 1 day, 2.5hr)
      if (subFormats.length > 1) {
        valueToFormat = valueParsing;
      } /* If there is only one formatter, don't round the unit (e.g. show 2.5s) */ else {
        valueToFormat = valueParsing;
        fractionDigits = shortNumberDisplay ? 1 : 2;
      }
    }
    string += formatDuration(valueToFormat, subFormat, fractionDigits);

    // if there are more subformats and a remainder, then add an empty space
    if (i !== subFormats.length - 1 && remainder) string += ' ';

    const subUnit = durationMap[subFormat];
    if (!subUnit.downFactor) {
      break;
    }
    // we can now take the remainder, and multiply it by the next down factor
    // eg. 1.15 hours, the integer is 1, and the remainder is 0.15
    // so we can translate the 0.15 hours into 9 minutes (0.15 * 60)
    valueParsing = remainder * subUnit.downFactor;
  }

  return string;
};

/*
 * This function takes in a float value (eg 1.123) and transforms it into a duration format compatible with
 * Excel/Google sheets CSVs (eg. 00:00:1.123).
 */
export const formatDurationForCSV = (value: number, format: DisplayDuration): string => {
  let seconds;

  switch (format.unit) {
    case DisplayDurationUnit.DURATION_MS:
      seconds = value / 1000;
      break;
    case DisplayDurationUnit.DURATION_MU:
      seconds = value / 1000000;
      break;
    case DisplayDurationUnit.DURATION_NS:
      seconds = value / 1000000000;
      break;
    case DisplayDurationUnit.DURATION_SEC:
    default:
      seconds = value;
      break;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  // Fractional should be formatted to 3 decimals places - call to slice(2) removes the leading '0.'
  // IE: 0.123 -> 123
  const fractionalSeconds = (seconds % 1).toFixed(3).slice(2);

  const pad = (num: number) => String(num).padStart(2, '0');

  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}.${fractionalSeconds}`;
};

function getIntegerAndRemainder(number: number): [number, number] {
  return [Math.floor(number), number % 1];
}

// Helper to get an array of time durations from a given number of subformats.
function getSubformats(currFormat: TimeDurations, numSubFormats: number): TimeDurations[] {
  const subFormats = [currFormat];
  let format = currFormat;
  for (let i = 0; i < numSubFormats; i++) {
    const downscaledFormat: TimeDurations | undefined = durationMap[format].down;
    if (downscaledFormat) {
      subFormats.push(downscaledFormat);
      format = downscaledFormat;
    } else {
      break;
    }
  }
  return subFormats;
}
